<script setup>
import { inject, computed, onMounted, onBeforeUnmount, ref } from 'vue';

import { cartKey } from '@/keys';

import { useCartFunctions } from '@/composables/useCartFunctions';
import useContext from '@/composables/useContext';
import { getWishlistProductsCount } from '@/composables/useWishlist';
import useWebsiteTexts from '@/composables/useWebsiteTexts';

import CountrySwitcher from '../CountrySwitcher.vue';

const { mitt, channel } = useContext();
const { isCartOpen } = useCartFunctions();
const { wishlistProductsCount } = getWishlistProductsCount();
const headerState = inject('headerState');
const cart = inject(cartKey, { value: {} });
const { websiteText } = useWebsiteTexts();

const searchButton = ref(null);

const isQuickCardEnabled = computed(() => cart?.value?.ecommerceFeaturesEnabled || false);
const invertColor = computed(() => (headerState.theme === 'white' && !headerState.scrolled ? 1 : 0));
const strokeColor = computed(() => (invertColor.value ? 'white' : 'black'));
const cartCount = computed(() => (cart.value?.rows || []).reduce((acc, c) => acc + c.quantity, 0));
const isCartCountOver99 = computed(() => cartCount.value > 99);

const wishlistPageUrl = computed(() => {
  const link = channel.value?.website?.wishlistPageLink || '';
  return link;
});

const transitionSpeed = headerState.speed;

function openQuickCart() {
  isCartOpen.value = !isCartOpen.value;
}

function focusSearchButton() {
  searchButton.value.focus();
}

onMounted(() => {
  mitt.on('close-search-input', focusSearchButton);
});

onBeforeUnmount(() => {
  mitt.off('close-search-input', focusSearchButton);
});
</script>

<template>
  <div class="header-actions">
    <button
      class="header-actions__item clean-button header-actions__item-search"
      ref="searchButton"
      @click="$mitt.emit('open-search-view')">
      <div
        class="header-actions__item-icon"
        :class="`header-actions__item-icon--inverted-${invertColor}`"
        :style="{ transition: `filter ${transitionSpeed} ease` }">
        <img
          class="header-actions__search-icon"
          src="../../static/icons/search_icon.svg"
          :alt="websiteText('header_actions__open_search').value"
        />
      </div>
    </button>
    <router-link
      :to="wishlistPageUrl"
      class="header-actions__item clean-button header-actions__favourites"
      v-if="wishlistPageUrl"
      ref="wishlistButton">
      <div
        class="header-actions__item-icon"
        :class="`header-actions__item-icon--inverted-${invertColor}`"
        :style="{transition:`filter ${transitionSpeed} ease`}"
      >
        <svg
            width="24"
            height="21"
            viewBox="0 0 24 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
          <path
              d="M20.8401 3.00645C20.3294 2.49545 19.7229 2.09009 19.0555 1.81353C18.388 1.53697 17.6726 1.39462 16.9501 1.39462C16.2276 1.39462 15.5122 1.53697 14.8448 1.81353C14.1773 2.09009 13.5709 2.49545 13.0601 3.00645L12.0001 4.06645L10.9401 3.00645C9.90843 1.97476 8.50915 1.39516 7.05012 1.39516C5.59109 1.39516 4.19181 1.97476 3.16012 3.00645C2.12843 4.03814 1.54883 5.43742 1.54883 6.89645C1.54883 8.35548 2.12843 9.75476 3.16012 10.7864L4.22012 11.8464L12.0001 19.6264L19.7801 11.8464L20.8401 10.7864C21.3511 10.2757 21.7565 9.66926 22.033 9.0018C22.3096 8.33434 22.4519 7.61894 22.4519 6.89645C22.4519 6.17396 22.3096 5.45855 22.033 4.79109C21.7565 4.12363 21.3511 3.5172 20.8401 3.00645V3.00645Z"
              stroke="black"
              stroke-width="1.25"
              stroke-linecap="round"
              stroke-linejoin="round"
          />
        </svg>
      </div>
      <div class="header-actions__heart-count">
        {{ wishlistProductsCount }}
      </div>
    </router-link>
    <button
      class="header-actions__item clean-button"
      @click="openQuickCart"
      v-if="isQuickCardEnabled">
      <div
        class="header-actions__item-icon"
        :class="`header-actions__item-icon--inverted-${invertColor}`"
        :style="{ transition: `filter ${transitionSpeed} ease` }">
        <svg
          width="18"
          height="21"
          viewBox="0 0 18 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <rect
            x="0.625"
            y="7.14258"
            width="16.75"
            height="12.8374"
            stroke="black"
            stroke-width="1.25" />
          <path
            d="M14.1404 9.71939V6.51771C14.1404 3.68852 11.8378 1.395 8.9975 1.395C6.15718 1.395 3.85464 3.68852 3.85464 6.51771V9.71939"
            stroke="black"
            stroke-width="1.25"
            stroke-linecap="round" />
        </svg>
      </div>
      <div
        class="header-actions__cart-count"
        :class="{'header-actions__cart-count-adjusted' : isCartCountOver99}"
      >
        {{ cartCount }}
      </div>
    </button>
    <CountrySwitcher
      class="header-actions__item desktop-only header-actions__country-switcher"
      :style="{ transition: `color ${transitionSpeed} ease`, color: strokeColor }" />
  </div>
</template>

<style>
  .header-actions {
    display: flex;
    padding-bottom: 5px;
    gap: 14px;

    @media (min-width: 840px) {
      margin-left: auto;
      padding-right: 15px;
      gap: 26px;
    }
  }

  .clean-button.header-actions__item {
    cursor: pointer;
    display: flex;
    align-items: center;
    position: relative;
  }

  .header-actions__item.header-actions__item-search {
    margin-right: -5px;
  }

  .header-actions__search-icon {
    height: 22px;
    padding-top: 2px;
    filter: brightness(0%);
    min-width: 23px;
  }

  .header-actions__favourite-icon {
    height: 22px;
    min-width: 23px;
    padding-top: 5px;
    filter: brightness(0%);
  }

  .header-actions__favourites {
    text-decoration: none;
  }

  .header-actions__favourite-icon .heart-icon {
    padding: 0px;
  }

  .header-actions__favourite-icon svg {
    width: 22px;
  }

  .header-actions__item-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 0px;
  }

  @media (max-width: 839px) {
    .header:not(.header--visible) .header-actions__item-icon--inverted-1 {
      filter: invert(1);
    }

    .header-actions__item.desktop-only {
      display: none;
    }

    .header-actions__item:nth-last-child(2) {
      margin-right: 0px;
    }

    .header-actions__cart-count.header-actions__cart-count-adjusted {
      left: 6px;
    }
  }

  @media (min-width: 840px) {
    .header:not(:hover):not(.header--visible) .header-actions__item-icon--inverted-1 {
      filter: invert(1);
    }
  }

  .header-actions__heart-count,
  .header-actions__cart-count {
    min-height: 15px;
    min-width: 15px;
    line-height: 14px;
    font-size: 14px;
    position: absolute;
    left: 12px;
    top: 0px;
    border-radius: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    color: black !important;
    padding: 0px 3px 1px 3px;
  }

  .header-actions__heart-count {
    left: 15px;
  }

  .header:hover .header-actions__country-switcher,
  .header.header--visible .header-actions__country-switcher {
    color: inherit !important;
  }

  .header-actions__item-label {
    @media (max-width: 767px) {
      display: none;
    }
  }
</style>
